import React, { useState } from "react";
import {
  ArrowRight,
  Award,
  Check,
  Copy,
  Gift,
  LoaderCircle,
  MessageCircle,
  Send,
  Sparkles,
  UsersRound,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";

const Referral = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState("");
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [asking, setAsking] = useState(false);
  const [assistantError, setAssistantError] = useState("");
  const referralLink = `${window.location.origin}/join`;
  const initials = (user?.name || "iTontine")
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  const inviteText = t("referral.shareMessage", { name: user?.name || "iTontine", link: referralLink });
  const whatsappLink = `https://wa.me/?text=${encodeURIComponent(inviteText)}`;

  const copyLink = async () => {
    setCopied(false);
    setCopyError("");
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
    } catch {
      setCopyError(t("referral.copyError"));
    }
  };

  const askAssistant = async (event) => {
    event.preventDefault();
    const submittedQuestion = question.trim();
    if (!submittedQuestion || asking) return;

    setQuestion("");
    setAssistantError("");
    setMessages((current) => [...current, { role: "user", text: submittedQuestion }]);
    setAsking(true);
    try {
      const { data } = await api.post("/member/assistant", { question: submittedQuestion });
      setMessages((current) => [...current, { role: "assistant", text: data.answer }]);
    } catch (error) {
      setAssistantError(error.response?.data?.message || t("common.error"));
    } finally {
      setAsking(false);
    }
  };

  const askSuggestedQuestion = () => {
    setQuestion(t("referral.suggestedQuestion"));
  };

  return (
    <main className="page-shell">
      <div className="grid items-stretch gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <section className="relative isolate flex flex-col overflow-hidden rounded-3xl bg-gradient-to-br from-[#1f3b2c] via-[#17382c] to-[#102b22] p-6 text-white shadow-lg sm:p-8">
          <div aria-hidden="true" className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full border border-white/10" />
          <div aria-hidden="true" className="pointer-events-none absolute -right-12 -top-16 h-48 w-48 rounded-full border border-gold-300/20" />
          <div className="relative flex flex-1 flex-col">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-gold-300">{t("referral.eyebrow")}</p>
            <h1 className="mt-3 max-w-xl font-serif text-3xl font-bold sm:text-4xl">{t("referral.title")}</h1>
            <p className="mt-3 max-w-xl text-sm leading-6 text-green-100">{t("referral.intro")}</p>

            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.07] p-4 sm:p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold">{t("referral.inviteLink")}</p>
                  <p className="mt-1 text-xs text-green-100/75">{t("referral.inviteLinkHelp")}</p>
                </div>
                <button
                  type="button"
                  onClick={copyLink}
                  aria-label={copied ? t("referral.copied") : t("referral.copyLink")}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gold-400 text-forest-deep transition hover:bg-gold-300"
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                </button>
              </div>
              <div className="mt-3 truncate rounded-xl bg-white px-3 py-3 text-sm font-medium text-forest">{referralLink}</div>
              {copied && <p className="mt-2 text-xs font-medium text-green-100">{t("referral.copied")}</p>}
              {copyError && <p role="alert" className="mt-2 text-xs font-medium text-rose-200">{copyError}</p>}
              <a
                href={whatsappLink}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#e9f8ef] px-3 py-2 text-xs font-semibold text-[#168447] transition hover:bg-[#d8f2e2]"
              >
                <MessageCircle size={15} />
                <span>{t("referral.whatsapp")}</span>
              </a>
            </div>

            <div className="mt-auto pt-6">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-gold-300">
                <Sparkles size={15} /> {t("referral.rewardJourney")}
              </div>
              <div className="mt-4 flex items-center">
                {[t("referral.inviteFriend"), t("referral.friendJoins"), t("referral.reward")].map((step, index) => (
                  <React.Fragment key={step}>
                    <div className="flex min-w-0 flex-1 flex-col items-center gap-2 text-center">
                      <span className={`flex h-9 w-9 items-center justify-center rounded-full border ${index === 2 ? "border-gold-300 bg-gold-400 text-forest-deep" : "border-white/25 bg-white/5 text-white"}`}>
                        {index === 2 ? <Gift size={16} /> : index + 1}
                      </span>
                      <span className="text-[11px] font-medium leading-4 text-green-50">{step}</span>
                    </div>
                    {index < 2 && <span aria-hidden="true" className="mb-5 h-px w-8 shrink-0 bg-white/25 sm:w-12" />}
                  </React.Fragment>
                ))}
              </div>
              <p className="mt-4 text-xs leading-5 text-green-100/75">{t("referral.trackingNotice")}</p>
            </div>
          </div>
        </section>

        <div className="grid auto-rows-fr gap-6">
          <section className="card flex flex-col p-6 shadow-sm sm:p-7">
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gold-50 text-gold-700"><Award size={21} /></span>
              <div>
                <h2 className="font-serif text-xl font-bold text-forest">{t("referral.rewardsTitle")}</h2>
                <p className="mt-1 text-sm text-gray-500">{t("referral.rewardsIntro")}</p>
              </div>
            </div>
            <div className="mt-5 rounded-2xl border border-gold-100 bg-gradient-to-br from-gold-50 to-white p-4 sm:p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-forest">{t("referral.freeMonthMilestone")}</p>
                  <p className="mt-1 text-xs leading-5 text-gray-500">{t("referral.milestoneDetail")}</p>
                </div>
                <Gift className="shrink-0 text-gold-600" size={22} />
              </div>
            </div>
            <div className="mt-auto pt-4 text-xs leading-5 text-gray-500">
              <Gift size={14} className="mr-1 inline text-gold-600" />
              {t("referral.rewardRules")}
            </div>
          </section>

          <section className="card flex flex-col p-6 shadow-sm sm:p-7">
            <div className="flex items-center gap-3">
              <UsersRound className="text-gold-600" size={21} />
              <h2 className="font-serif text-xl font-bold text-forest">{t("referral.leaderboardTitle")}</h2>
            </div>
            <div className="mt-5 flex items-center gap-3 rounded-2xl border border-gold-100 bg-paper p-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-forest text-sm font-bold text-white">{initials}</span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-forest">{user?.name || t("referral.you")}</p>
                <p className="mt-1 text-xs leading-5 text-gray-500">{t("referral.leaderboardEmpty")}</p>
              </div>
            </div>
            <div className="mt-auto flex items-center gap-2 pt-4 text-xs text-gray-500">
              <UsersRound size={14} className="shrink-0 text-gold-600" />
              {t("referral.leaderboardTrackingNotice")}
            </div>
          </section>
        </div>
      </div>

      <section className="card mt-6 p-6 shadow-sm sm:p-7">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold-50 text-gold-700"><Sparkles size={21} /></span>
            <div>
              <h2 className="font-serif text-xl font-bold text-forest">{t("referral.assistantTitle")}</h2>
              <p className="mt-1 text-sm text-gray-500">{t("referral.assistantIntro")}</p>
            </div>
          </div>
          <button type="button" onClick={askSuggestedQuestion} className="inline-flex min-h-9 w-fit items-center gap-2 rounded-full border border-gold-200 bg-gold-50 px-3 py-2 text-xs font-semibold text-gold-700 transition hover:bg-gold-100">
            {t("referral.suggestedQuestion")} <ArrowRight size={13} />
          </button>
        </div>

        {messages.length > 0 && (
          <div aria-live="polite" className="mt-5 max-h-56 space-y-3 overflow-y-auto rounded-2xl bg-paper p-4">
            {messages.map((item, index) => (
              <div key={`${item.role}-${index}`} className={`max-w-[90%] rounded-2xl px-4 py-3 text-sm leading-6 ${item.role === "user" ? "ml-auto bg-forest text-white" : "bg-white text-gray-700 shadow-sm"}`}>
                {item.text}
              </div>
            ))}
            {asking && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <LoaderCircle className="animate-spin text-gold-600" size={16} /> {t("referral.assistantThinking")}
              </div>
            )}
          </div>
        )}
        {assistantError && <p role="alert" className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">{assistantError}</p>}

        <form onSubmit={askAssistant} className="mt-5 flex gap-2">
          <input
            className="input"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            maxLength={400}
            placeholder={t("referral.questionPlaceholder")}
            aria-label={t("referral.questionPlaceholder")}
          />
          <button type="submit" className="btn-primary shrink-0 px-4" disabled={asking || !question.trim()} aria-label={t("referral.askAssistant")}>
            {asking ? <LoaderCircle className="animate-spin" size={17} /> : <Send size={17} />}
          </button>
        </form>
      </section>
    </main>
  );
};

export default Referral;
